import type { LocationPoint } from './types';

export function primary(point?: LocationPoint | null) {
  if (!point) return 'No confirmed location yet';
  // Walks the same ladder the server resolved down, so a partially resolved
  // point still names a place. "Location confirmed" is the last resort only:
  // whenever enrichment produced anything at all, the title says where.
  return point.primaryLabel || point.building || point.road || point.neighbourhood
    || point.city || point.lga || point.state || point.country || 'Location confirmed';
}
export function secondary(point?: LocationPoint | null) {
  if (!point) return '';
  if (point.secondaryLabel) return point.secondaryLabel;
  return [point.neighbourhood, point.city, point.state].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(', ');
}
// Nigerian directions are relational, not cardinal: "opposite the filling
// station" is an address, "801 m northeast of it" is a coordinate in disguise.
export function landmark(point?: LocationPoint | null) {
  if (!point?.landmarkName) return '';
  // When the anchor is close enough to have become the headline, repeating it
  // underneath just says the same thing twice.
  if (point.primaryLabel?.includes(point.landmarkName)) return '';
  const relation = point.landmarkRelation || 'near';
  const phrase = `${relation[0].toUpperCase()}${relation.slice(1)} ${point.landmarkName}`;
  // Only worth quoting a distance when it is far enough to matter.
  const far = (point.landmarkDistanceMeters ?? 0) > 60;
  return far ? `${phrase} · ${fmtDistance(point.landmarkDistanceMeters)}` : phrase;
}
export function fmtDistance(metersValue?: number | null) {
  if (metersValue == null) return '';
  return metersValue < 1000 ? `approx. ${Math.round(metersValue)} m` : `${(metersValue / 1000).toFixed(metersValue >= 10000 ? 0 : 1)} km`;
}
export function accuracy(point?: LocationPoint | null) { return point?.accuracy == null ? 'Accuracy unavailable' : `Accuracy ±${Math.round(point.accuracy)} m`; }
export function meters(a: LocationPoint, b: LocationPoint) {
  const R = 6371000, rad = Math.PI / 180; const dLat = (b.latitude - a.latitude) * rad; const dLon = (b.longitude - a.longitude) * rad;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(a.latitude * rad) * Math.cos(b.latitude * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}
export function fromViewer(point?: LocationPoint | null, viewer?: LocationPoint | null) { return point && viewer ? `${fmtDistance(meters(viewer, point)).replace('approx. ', '')} from you` : ''; }
/**
 * Home is a list of people. This collapses a member's device registrations down
 * to the single device the server chose to represent them, so someone who has
 * signed in from four browsers still appears once.
 *
 * A member with no primary device is omitted entirely: no native phone, and no
 * browser open in front of them right now.
 */
export function homeMembers<M extends {
  primaryDeviceId?: string | null;
  role: string;
  access?: { authorized: boolean } | undefined;
  user: { id: string; name: string; devices: Array<{ id: string }> };
}>(members: M[]): Array<{ member: M; device: M['user']['devices'][number] }> {
  const selected: Array<{ member: M; device: M['user']['devices'][number] }> = [];
  for (const member of members) {
    const device = member.user.devices.find(d => d.id === member.primaryDeviceId);
    if (device) selected.push({ member, device });
  }
  return selected;
}

export function initials(name: string) { return name.split(/\s+/).filter(Boolean).slice(0, 2).map(v => v[0]?.toUpperCase()).join('') || '?'; }
